import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useTrash — live subscription to the user's trash collection
 * (trash/{uid}/items). Sorted by deletedAt desc so the most-recent
 * deletion is at the top of the trash UI.
 *
 * Returns { items, loading } where each item has:
 *   - id (trash doc id)
 *   - kind: 'board' | 'list'
 *   - payload: full snapshot used by restoreFromTrash to re-create
 *   - deletedAt, expiresAt, deletedBy, originalParent
 *
 * Items past expiresAt are hard-deleted by the daily
 * `expireTrashItems` Cloud Function — they'll silently disappear
 * from the subscription as the sweep runs (max 24h lag from
 * expiry).
 */
export function useTrash(userId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    const q = query(
      collection(db, 'trash', userId, 'items'),
      orderBy('deletedAt', 'desc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.warn('[useTrash] snapshot error', err);
      setItems([]);
      setLoading(false);
    });
    return unsub;
  }, [userId]);

  return { items, loading };
}
